import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Typography, Button, Avatar, Paper, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { logout } from '../../utils/auth/session';
import { NAVBAR_MENU_KEYS, NS } from '../../i18n/constants/keys';
import { translateRole } from '../../i18n/helpers/translateRole';
import ViewOption from './ViewOption';

export const MobileProfile: React.FC = () => {
  const { t } = useTranslation(NS.navbarMenus);
  const history = useHistory();

  const [profilePic, setProfilePic] = useState<string>(() => {
    const raw = localStorage.getItem('profilePic') || '';
    return !raw || raw === 'null' || raw === 'undefined' || !raw.trim() ? '' : raw.trim();
  });
  const [firstName, setFirstName] = useState<string>(() => localStorage.getItem('firstname') || '');
  const [lastName, setLastName] = useState<string>(() => localStorage.getItem('lastname') || '');
  const [email, setEmail] = useState<string>(() => localStorage.getItem('email') || '');
  const role = localStorage.getItem('role') || 'Candidate';

  useEffect(() => {
    const handleProfileUpdate = (e?: Event) => {
      const customEvent = e as CustomEvent<{ profilePic?: string; firstName?: string; lastName?: string; email?: string }>;
      const newPic = customEvent?.detail?.profilePic ?? (localStorage.getItem('profilePic') || '');
      const cleanPic = !newPic || newPic === 'null' || newPic === 'undefined' || !newPic.trim() ? '' : newPic.trim();
      setProfilePic(cleanPic);

      const fn = customEvent?.detail?.firstName ?? (localStorage.getItem('firstname') || '');
      const ln = customEvent?.detail?.lastName ?? (localStorage.getItem('lastname') || '');
      const em = customEvent?.detail?.email ?? (localStorage.getItem('email') || '');
      setFirstName(fn);
      setLastName(ln);
      setEmail(em);
    };

    window.addEventListener('profilePicUpdated' as any, handleProfileUpdate);
    window.addEventListener('userProfileUpdated' as any, handleProfileUpdate);
    window.addEventListener('storage', handleProfileUpdate);

    return () => {
      window.removeEventListener('profilePicUpdated' as any, handleProfileUpdate);
      window.removeEventListener('userProfileUpdated' as any, handleProfileUpdate);
      window.removeEventListener('storage', handleProfileUpdate);
    };
  }, []);

  const user = {
    name: `${firstName} ${lastName}`.trim() || 'User',
    avatar: profilePic,
    initials: `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U',
    role,
    email,
  };

  const hasAvatar =
    Boolean(user.avatar) &&
    user.avatar !== 'null' &&
    user.avatar !== 'undefined' &&
    user.avatar.trim() !== '';

  const logOut = async () => {
    await logout();
    history.push('/login');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={hasAvatar ? user.avatar : undefined}
            sx={(theme) => ({
              width: 48,
              height: 48,
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              color: theme.palette.primary.main,
              fontWeight: 800,
              fontSize: '1rem',
              border: '1px solid',
              borderColor: 'divider',
            })}
          >
            {!hasAvatar && user.initials}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 800, color: 'text.primary' }}>
              {user.name}
            </Typography>
            {user.email && (
              <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block' }}>
                {user.email}
              </Typography>
            )}
            <Chip
              size="small"
              label={translateRole(t, user.role)}
              sx={(theme) => ({
                mt: 0.5,
                height: 22,
                fontSize: '0.6875rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              })}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => history.push('/user-profile')}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8125rem',
              py: 0.75,
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
              },
            }}
          >
            {t(NAVBAR_MENU_KEYS.profile.items.profileSettings.title)}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => history.push('/change-password')}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8125rem',
              py: 0.75,
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
              },
            }}
          >
            {t(NAVBAR_MENU_KEYS.profile.items.changePassword.title)}
          </Button>
        </Box>

        <Button
          fullWidth
          variant="contained"
          size="small"
          onClick={logOut}
          sx={(theme) => ({
            mt: 1.5,
            textTransform: 'none',
            fontWeight: 700,
            py: 0.85,
            bgcolor: alpha(theme.palette.error.main, 0.08),
            color: 'error.main',
            boxShadow: 'none',
            border: '1px solid',
            borderColor: alpha(theme.palette.error.main, 0.2),
            '&:hover': {
              bgcolor: alpha(theme.palette.error.main, 0.15),
              boxShadow: 'none',
            },
          })}
        >
          {t(NAVBAR_MENU_KEYS.profile.logout)}
        </Button>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'text.secondary',
            mb: 1.5,
          }}
        >
          {t(NAVBAR_MENU_KEYS.profile.settings)}
        </Typography>
        <ViewOption />
      </Paper>
    </Box>
  );
};
